import { useMemo } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { EmptyValue } from '@/components/custom/empty-value'
import { TOOLTIP_DELAY_MS } from '@/constants/timing'
import { createMemoCell } from '@/lib/create-memo-cell'
import { cn } from '@/lib/utils'
import type { CellProps } from '../types'

interface CellDateProps extends CellProps<string | Date | null | undefined> {
  /** Format type */
  format?: 'date' | 'datetime' | 'time' | 'relative'
  /** Locale for formatting */
  locale?: string
}

function CellDateBase({
  value,
  format = 'date',
  locale = 'en-US',
  className,
}: CellDateProps) {
  const { displayValue, fullValue } = useMemo(() => {
    if (value === null || value === undefined || value === '') {
      return { displayValue: null, fullValue: null }
    }

    const date = value instanceof Date ? value : new Date(value)

    if (isNaN(date.getTime())) {
      return { displayValue: null, fullValue: null }
    }

    try {
      let displayValue: string
      let fullValue: string

      switch (format) {
        case 'datetime':
          displayValue = new Intl.DateTimeFormat(locale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }).format(date)
          fullValue = new Intl.DateTimeFormat(locale, {
            dateStyle: 'full',
            timeStyle: 'long',
          }).format(date)
          break

        case 'time':
          displayValue = new Intl.DateTimeFormat(locale, {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          }).format(date)
          fullValue = displayValue
          break

        case 'relative':
          displayValue = getRelativeTimeString(date, locale)
          fullValue = new Intl.DateTimeFormat(locale, {
            dateStyle: 'full',
            timeStyle: 'short',
          }).format(date)
          break

        default:
          // Use UTC for date-only fields to avoid timezone shifts
          displayValue = new Intl.DateTimeFormat(locale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC',
          }).format(date)
          fullValue = new Intl.DateTimeFormat(locale, {
            dateStyle: 'full',
            timeZone: 'UTC',
          }).format(date)
      }

      return { displayValue, fullValue }
    } catch {
      return { displayValue: String(value), fullValue: String(value) }
    }
  }, [value, format, locale])

  if (displayValue === null) {
    return <EmptyValue />
  }

  return (
    <Tooltip delayDuration={TOOLTIP_DELAY_MS}>
      <TooltipTrigger asChild>
        <span className={cn('block truncate', className)}>{displayValue}</span>
      </TooltipTrigger>
      <TooltipContent side="bottom" sideOffset={4}>
        {fullValue}
      </TooltipContent>
    </Tooltip>
  )
}

function getRelativeTimeString(date: Date, locale: string): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSecs = Math.round(diffMs / 1000)
  const diffMins = Math.round(diffSecs / 60)
  const diffHours = Math.round(diffMins / 60)
  const diffDays = Math.round(diffHours / 24)
  const diffWeeks = Math.round(diffDays / 7)
  const diffMonths = Math.round(diffDays / 30)
  const diffYears = Math.round(diffDays / 365)

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })

  if (Math.abs(diffSecs) < 60) {
    return rtf.format(-diffSecs, 'second')
  } else if (Math.abs(diffMins) < 60) {
    return rtf.format(-diffMins, 'minute')
  } else if (Math.abs(diffHours) < 24) {
    return rtf.format(-diffHours, 'hour')
  } else if (Math.abs(diffDays) < 7) {
    return rtf.format(-diffDays, 'day')
  } else if (Math.abs(diffWeeks) < 4) {
    return rtf.format(-diffWeeks, 'week')
  } else if (Math.abs(diffMonths) < 12) {
    return rtf.format(-diffMonths, 'month')
  } else {
    return rtf.format(-diffYears, 'year')
  }
}

export const CellDate = createMemoCell(CellDateBase, 'CellDate')
